<script lang="ts" setup>
import type { IAppProps } from '@/interfaces/IAppProps'
import type { BonusWalletData } from '@/interfaces/IBonusWallet'
import { onClickOutside, useIntervalFn } from '@vueuse/core'
import { computed, nextTick, onMounted, onUnmounted, ref, watchEffect } from 'vue'
import { balanceApi, getBonusWalletDetailApi } from '@/apis/balanceApi'
import BonusWallet from '@/components/bonus-wallet/BonusWallet.vue'
import IconArrowExpandable from '@/components/icons/IconArrowExpandable.vue'
import { useEntryProps } from '@/composable/useDatasetProps'
import { useGlobalStore } from '@/stores/globalStore'

const globalStore = useGlobalStore()

const EXPANDABLE_CONTENT_CONSTANTS = {
    MIN_MARGIN: 12,
    DESKTOP_BREAKPOINT: 1024,
    MAX_WIDTH: 400,
    MOBILE_PADDING: 32,
} as const

const BALANCE_REFRESH_INTERVAL_MS = 30000

const DROPDOWN_ARROW_DEFAULT_POSITION = '50%'

const balance = ref<string>('')
const isExpanded = ref(false)
const navListTop = ref<number>(0)
const bonusWalletData = ref<BonusWalletData>({ mainWalletBalance: 0, bonusItems: [] })
const expandableContentStyle = ref<{ right?: string, left?: string, transform?: string, width?: string }>({})
const dropdownArrowLeft = ref<string>(DROPDOWN_ARROW_DEFAULT_POSITION)
const expandableContentRef = ref<HTMLElement>()
const arrowToggleRef = ref<HTMLElement>()

const { loginName } = useEntryProps<IAppProps>()

const hasBonusWalletData = computed(() => {
    if (!bonusWalletData.value) return false
    return bonusWalletData.value.bonusItems.some(item => item.walletBalance > 0)
})

const shouldShowArrowToggle = computed(() => {
    return globalStore.mainToggle.toggleDisplayBonusWalletAmount && hasBonusWalletData.value
})

const updateNavListPosition = () => {
    const navList = document.querySelector('.nav-list') as HTMLElement
    if (navList) {
        navListTop.value = navList.getBoundingClientRect().top
    }
}

const updateDropdownArrowPosition = () => {
    const balanceTxt = document.getElementById('balance-txt')
    const balanceContainer = document.querySelector('.balance') as HTMLElement
    if (balanceTxt && balanceContainer) {
        const balanceTxtRect = balanceTxt.getBoundingClientRect()
        const containerRect = balanceContainer.getBoundingClientRect()
        const balanceTxtCenter = balanceTxtRect.left + balanceTxtRect.width / 2
        const containerLeft = containerRect.left
        const relativeLeft = balanceTxtCenter - containerLeft
        dropdownArrowLeft.value = `${relativeLeft}px`
    }
    else {
        dropdownArrowLeft.value = DROPDOWN_ARROW_DEFAULT_POSITION
    }
}

function getDefaultContentWidth(windowWidth: number): number {
    const isDesktop = windowWidth >= EXPANDABLE_CONTENT_CONSTANTS.DESKTOP_BREAKPOINT
    if (isDesktop) {
        return EXPANDABLE_CONTENT_CONSTANTS.MAX_WIDTH
    }
    return Math.min(windowWidth - EXPANDABLE_CONTENT_CONSTANTS.MOBILE_PADDING, EXPANDABLE_CONTENT_CONSTANTS.MAX_WIDTH)
}

function getExpandableContentWidth(windowWidth: number, expandableContent: HTMLElement | null): number {
    const defaultWidth = getDefaultContentWidth(windowWidth)

    if (expandableContent) {
        const rect = expandableContent.getBoundingClientRect()
        return rect.width || defaultWidth
    }

    return defaultWidth
}

function calculateAlignedPosition(windowWidth: number, arrowToggleRight: number, width: number) {
    return {
        right: `${windowWidth - arrowToggleRight}px`,
        left: 'auto',
        transform: 'none',
        width: `${width}px`,
    }
}

function calculateCenteredPosition(windowWidth: number) {
    const centeredWidth = Math.min(
        windowWidth - EXPANDABLE_CONTENT_CONSTANTS.MIN_MARGIN * 2,
        EXPANDABLE_CONTENT_CONSTANTS.MAX_WIDTH,
    )
    return {
        left: '50%',
        right: 'auto',
        transform: 'translateX(-50%)',
        width: `${centeredWidth}px`,
    }
}

const updateExpandableContentPosition = async () => {
    if (!isExpanded.value) {
        return
    }

    const arrowToggle = arrowToggleRef.value
    const expandableContent = expandableContentRef.value ?? null

    if (!arrowToggle) {
        return
    }

    // Wait for DOM to be fully rendered, especially for the first render
    await nextTick()
    requestAnimationFrame(() => {
        const windowWidth = window.innerWidth
        const contentWidth = getDefaultContentWidth(windowWidth)
        const expandableWidth = getExpandableContentWidth(windowWidth, expandableContent)
        const arrowToggleRight = arrowToggle.getBoundingClientRect().right
        const requiredLeftSpace = expandableWidth + EXPANDABLE_CONTENT_CONSTANTS.MIN_MARGIN
        const availableLeftSpace = arrowToggleRight

        if (availableLeftSpace >= requiredLeftSpace) {
            expandableContentStyle.value = calculateAlignedPosition(windowWidth, arrowToggleRight, contentWidth)
        }
        else {
            expandableContentStyle.value = calculateCenteredPosition(windowWidth)
        }
    })
}

const fetchBonusWalletData = async () => {
    try {
        const fullWalletInfo = await getBonusWalletDetailApi.get()
        bonusWalletData.value = {
            mainWalletBalance: fullWalletInfo.mainWalletBalance,
            bonusItems: fullWalletInfo.bonusWallets.map((wallet, index) => ({
                id: `bonus-wallet-${index}`,
                promotionTitle: wallet.promotionTitle,
                currentTurnover: wallet.currentTurnover,
                targetTurnover: wallet.targetTurnover,
                walletBalance: wallet.walletBalance,
                progressPercentage: wallet.targetTurnover > 0
                    ? Math.round((wallet.currentTurnover / wallet.targetTurnover) * 100)
                    : 0,
            })),
        }
    }
    catch (error) {
        console.error('Failed to fetch bonus wallet data:', error)
    }
}

const toggleExpand = async () => {
    isExpanded.value = !isExpanded.value
    if (isExpanded.value) {
        updateNavListPosition()
        await nextTick()
        updateDropdownArrowPosition()
        await updateExpandableContentPosition()
        if (!bonusWalletData.value) {
            fetchBonusWalletData()
        }
    }
}

const handleWindowResize = async () => {
    updateNavListPosition()
    updateDropdownArrowPosition()
    await updateExpandableContentPosition()
}

const handleWindowScroll = async () => {
    updateNavListPosition()
    updateDropdownArrowPosition()
    await updateExpandableContentPosition()
}

const updateBalance = async () => {
    const newBalance = await balanceApi.get()
    if (balance.value !== newBalance) {
        balance.value = newBalance
        globalStore.setBalance(newBalance)
    }
}

const { resume } = useIntervalFn(updateBalance, BALANCE_REFRESH_INTERVAL_MS, { immediate: false })

onMounted(async () => {
    updateNavListPosition()
    updateDropdownArrowPosition()
    updateExpandableContentPosition()

    const initialBalance = await balanceApi.get()
    balance.value = initialBalance
    globalStore.setBalance(initialBalance)

    if (globalStore.mainToggle.toggleDisplayBonusWalletAmount) {
        fetchBonusWalletData()
    }

    window.addEventListener('resize', handleWindowResize)
    window.addEventListener('scroll', handleWindowScroll)

    resume()
})

onUnmounted(() => {
    window.removeEventListener('resize', handleWindowResize)
    window.removeEventListener('scroll', handleWindowScroll)
})

// Handle expand/collapse state changes and event listeners
watchEffect(async () => {
    if (isExpanded.value) {
        updateNavListPosition()
        await nextTick()
        updateDropdownArrowPosition()
        await updateExpandableContentPosition()
    }
})

// Handle click outside to close expandable content
// Only activate when expanded, and ignore clicks on arrow toggle
onClickOutside(
    expandableContentRef,
    () => {
        if (isExpanded.value) {
            isExpanded.value = false
        }
    },
    {
        ignore: [arrowToggleRef],
    },
)

// Update dropdown arrow position when balance changes while expanded
watchEffect(async () => {
    if (balance.value && isExpanded.value) {
        await nextTick()
        updateDropdownArrowPosition()
        await updateExpandableContentPosition()
    }
})

// Recalculate position when bonus wallet data loads while expanded
watchEffect(async () => {
    if (bonusWalletData.value && isExpanded.value) {
        await nextTick()
        await updateExpandableContentPosition()
    }
})

const reloadBalance = async () => {
    const balanceText = document.getElementById('balance-txt')
    balanceText?.classList.remove('blink')

    const refreshIcon = document.getElementById('icon-refresh')
    refreshIcon?.classList.toggle('icon-spin')

    balance.value = await balanceApi.get()

    if (globalStore.mainToggle.toggleDisplayBonusWalletAmount) {
        await fetchBonusWalletData()
    }

    refreshIcon?.classList.toggle('icon-spin')
    balanceText?.classList.toggle('blink')
    await nextTick()
    if (isExpanded.value) {
        updateDropdownArrowPosition()
    }
}
</script>

<template>
    <div class="user-info">
        <div class="login-name">
            <img alt="user-icon" class="icon-user" src="../../assets/images/icon_user.svg">
            <span class="username-txt">{{ loginName }}</span>
        </div>
        <div class="balance-container">
            <div class="balance balance-refresh" :class="{ 'show-dropdown-arrow': isExpanded && shouldShowArrowToggle }" :style="{ '--arrow-left': dropdownArrowLeft }" @click="reloadBalance()">
                <div ref="arrowToggleRef" :class="{ 'arrow-toggle': shouldShowArrowToggle }" @click.stop="shouldShowArrowToggle && toggleExpand()">
                    <span id="balance-txt" class="balance-txt">
                        <span id="balance">{{ balance }}</span>
                    </span>
                    <IconArrowExpandable v-if="shouldShowArrowToggle" :expanded="isExpanded" />
                </div>
                <img id="icon-refresh" alt="reload" class="icon-refresh" src="../../assets/images/icon_reload.svg">
            </div>
            <div v-show="isExpanded" ref="expandableContentRef" class="expandable-content" :style="{ top: `${navListTop}px`, ...expandableContentStyle }">
                <BonusWallet :data="bonusWalletData" />
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@use "@/assets/styles/common/variables.scss" as *;

.user-info {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    line-height: 16px;

    .login-name,
    .balance {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
    }

    .balance {
        position: relative;
        cursor: pointer;

        &.show-dropdown-arrow::after {
            position: absolute;
            z-index: 5;
            bottom: -15px;
            left: var(--arrow-left, 50%);
            width: 10px;
            height: 10px;
            box-sizing: border-box;
            border-top: 10px solid $indigo-100;
            border-left: 10px solid $indigo-100;
            content: "";
            transform: translateX(-50%) rotate(45deg);
        }
    }
}

.username-txt {
    display: inline-block;
    color: #ffffff;
    font-weight: bold;
}

.icon-user {
    width: 16px;
}

.balance-container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.balance-refresh:hover span,
.balance-refresh:hover img {
    color: #ffffff;
}

.balance-refresh.refresh-loading:hover span,
.balance-refresh.refresh-loading:hover img {
    color: #ffffff;
}

.balance-refresh .icon-refresh {
    width: 16px;
}

.arrow-toggle {
    display: flex;
    align-items: center;
    color: #ffffff;
    cursor: pointer;
    gap: 8px;
}

.balance-txt {
    display: inline-block;
    color: #ffffff;
    font-weight: bold;
}

.expandable-content {
    position: fixed;
    z-index: 4;
    display: flex;
    overflow: hidden;
    box-sizing: border-box;
    flex-direction: column;
    align-items: center;
    padding: 0;
    border-radius: 8px;
    background-color: #ffffff;
    box-shadow: 0 0 12px 0 rgb(0, 0, 0, 16%), 0 0 4px 0 rgb(0, 0, 0, 12%);
}

.icon-spin {
    display: inline-block;
    animation: spin 2s infinite linear;
}

.blink {
    animation: blink 1s;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(359deg);
    }
}

@keyframes blink {
    0% {
        opacity: 1;
    }

    50% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
}
</style>
